// Per-day NURSE-TRANSFER overlay (operatingRoomId -> toNurseId), local to the Board — the CRNA analogue of the
// doctor room-transfer overlay in roomTransfers.ts. Moving/swapping a CRNA in their person card writes here; the
// Board then shows that room's CRNA as the target nurse instead of the allocated one, and every read of a room's
// CRNA (nameplate, bullpen, concurrency, supervision, the CRNA card) goes through effectiveNurseIdsForRoom so they
// all agree. Reversible — clearing removes the entry. Stored per day (survives refresh), like the room transfers.
export const nurseTransfersKey = (date: string) => `oc-nurse-transfers-${date}`;

// Sentinel overlay value meaning "this room's allocated CRNA has LEFT and nobody replaced them". A plain
// `delete` cannot say this — it reverts the room to its raw allocation — so without a vacancy marker a genuine
// MOVE off a raw-allocated room is impossible to express and degrades into a CLONE (the CRNA shows on both the
// room they moved to and the room they were allocated to). Chosen to be un-collidable with an employeeId (all
// of which are UUIDs) so a persisted overlay from before this existed can never be misread as a vacancy.
export const NURSE_TRANSFER_VACANT = '__vacant__';

export function loadNurseTransfers(date: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(nurseTransfersKey(date));
    // Accept only a plain object map; reject a JSON array (typeof [] === 'object') or any other shape so a
    // corrupt entry falls back to an empty overlay instead of poisoning room-nurse lookups.
    if (raw) { const v = JSON.parse(raw); if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, string>; }
  } catch { /* ignore */ }
  return {};
}

// Apply the overlay to one room's nurse list: a MOVED room reports its TARGET CRNA in place of the allocated
// CRNA, while any STUDENT (non-CRNA nurse-type) on the case is preserved — a CRNA swap only trades the CRNA,
// never the student. An un-moved room reports its allocated nurses unchanged. `isCrna` tells CRNAs (the thing
// the overlay replaces) apart from students. This is the single semantics every board read site goes through
// so nameplate, supervision, concurrency, bullpen and the CRNA card all agree on who owns a room. Pure +
// exported for unit testing.
export function effectiveNurseIdsForRoom(
  allocatedNurseIds: string[],
  operatingRoomId: string | undefined,
  transfers: Record<string, string>,
  isCrna: (id: string) => boolean,
): string[] {
  const to = operatingRoomId ? transfers[operatingRoomId] : undefined;
  if (to === undefined) return allocatedNurseIds;
  const students = allocatedNurseIds.filter(id => !isCrna(id));
  // A VACATED room reports no CRNA at all (its allocated one moved elsewhere), but still keeps its student.
  return to === NURSE_TRANSFER_VACANT ? students : [to, ...students];
}

// Move a room to a CRNA (toNurseId) or clear the move (null); returns the updated map.
export function setNurseTransfer(date: string, operatingRoomId: string, toNurseId: string | null): Record<string, string> {
  const t = loadNurseTransfers(date);
  if (toNurseId) t[operatingRoomId] = toNurseId; else delete t[operatingRoomId];
  try { localStorage.setItem(nurseTransfersKey(date), JSON.stringify(t)); } catch { /* ignore */ }
  return t;
}

// Persist a whole overlay map computed elsewhere (see nurseRoomMoves.ts, where the move/swap/remove rules live
// as pure functions). setNurseTransfer edits ONE key at a time, which cannot express a move that both seats a
// CRNA on one room and releases them from another — the two writes have to land together.
export function saveNurseTransfers(date: string, transfers: Record<string, string>): Record<string, string> {
  try { localStorage.setItem(nurseTransfersKey(date), JSON.stringify(transfers)); } catch { /* ignore */ }
  return transfers;
}

// Remove a CRNA from EVERY room they hold in the overlay, by VALUE rather than by key — the analogue of
// clearFloatCrnaForEmployee in floatCrnas.ts. Used when a person is placed somewhere else (a board drop, a
// card move), so a stale entry on their previous room can never leave them showing in two ORs at once. Note
// this only clears OVERLAY entries: a CRNA held by a raw allocation needs a vacancy (see nurseRoomMoves.ts).
export function clearNurseTransferForEmployee(date: string, employeeId: string): Record<string, string> {
  const t = loadNurseTransfers(date);
  let changed = false;
  for (const roomId of Object.keys(t)) if (t[roomId] === employeeId) { delete t[roomId]; changed = true; }
  if (changed) { try { localStorage.setItem(nurseTransfersKey(date), JSON.stringify(t)); } catch { /* ignore */ } }
  return t;
}

// Clear EVERY nurse transfer for a date. Used when a new simulation replaces the day, so old room-to-CRNA
// moves (keyed by the stable operatingRoomId) don't linger on the fresh board — mirrors clearRoomTransfers.
export function clearNurseTransfers(date: string): void {
  try { localStorage.removeItem(nurseTransfersKey(date)); } catch { /* ignore */ }
}
